/** ***********
*
* <chart.base.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActiveElement, Chart, ChartEvent, ChartType, DefaultDataPoint } from 'chart.js';
import Annotation from 'chartjs-plugin-annotation';
import 'chartjs-adapter-moment';
import { CommonKPIService } from '../../kpi/kpi-common';
import { GraphSize, Ng2ChartConfiguration } from './chart.model';
import { BaseChartDirective } from 'ng2-charts';

export interface ChartClickEvent<O = Record<string, any>> {
  event: ChartEvent & { chart?: Chart };
  item: ActiveElement[];
  others?: O;
}

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export default abstract class ChartBaseComponent<
  TDataKpi,
  TType extends ChartType,
  TLabel = unknown
> implements OnChanges {

  @Input() public dataKPI: TDataKpi;
  @Output() chartClicked = new EventEmitter<ChartClickEvent>();
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  abstract chartConfiguration: Ng2ChartConfiguration<TType, DefaultDataPoint<TType>, TLabel>;
  public graphSize?: GraphSize = 'auto';
  public height = 500;

  constructor(
    protected _translate: TranslateService,
    protected _cd: ChangeDetectorRef,
    protected _commonKPIService: CommonKPIService,
  ) {
    Chart.register(Annotation);
  }

  /**
   * @returns True if the kpis are already refreshed
   */
  ngOnChanges({ dataKPI, loading }: SimpleChanges): boolean|void {
    if (dataKPI) {
      if (dataKPI.currentValue !== dataKPI.previousValue) {
        this._refreshKpis();
        this._cd.markForCheck();
        return true;
      }
    }
    if (loading) {
      if (loading.currentValue !== loading.previousValue) {
        this._cd.markForCheck();
        return true;
      }
    }
  }

  private _refreshKpis(): void {
    this.setKpiData();
  }

  /**
   * Set the data for the KPI, called when a changed in detected in the Input data
   */
  abstract setKpiData(): void;

}
